import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { createClient } from '@/lib/supabase-client'
import Sidebar from '@/components/Sidebar'
import DashboardHeader from '@/components/DashboardHeader'

interface Tool {
  id: string
  name: string
  slug: string
  description: string | null
  credit_cost: number
  is_active: boolean
  created_at: string
  updated_at: string
}

interface ToolAnalytics {
  by_tool_all_time: Record<string, { name: string; slug: string; count: number; credits: number }>
  by_tool_this_month: Record<string, { name: string; slug: string; count: number; credits: number }>
  by_tool_and_source: Record<string, Record<string, { count: number; credits: number }>>
}

interface ShopifyAnalytics {
  total_shops: number
  active_subscriptions: number
  total_revenue: number
  total_one_time_purchases: number
  usage_this_month: number
  usage_all_time: number
  shops: any[]
  subscriptions: any[]
  payments: any[]
  one_time_purchases: any[]
}

interface AdminStats {
  summary: {
    total_users: number
    active_subscriptions: number
    total_credits_earned: number
    total_credits_spent: number
    total_credits_balance: number
    total_usage_all_time: number
    total_usage_this_month: number
    usage_by_source: Record<string, number>
  }
  tools?: Tool[]
  tool_analytics?: ToolAnalytics
  shopify_analytics?: ShopifyAnalytics
  users: Array<{
    id: string
    name: string | null
    email: string | null
    api_key: string | null
    created_at: string
    credits: {
      balance: number
      total_earned: number
      total_spent: number
    }
    subscription: {
      status: string
      plan_name: string
    } | null
    usage: {
      this_month: number
      all_time: number
    }
  }>
}

interface SubscriptionPlan {
  id: string
  name: string
  stripe_product_id: string | null
  stripe_price_id_monthly: string | null
  credits_per_period: number
  price_monthly_cents: number
  is_active: boolean
  sort_order: number
}

interface CreditPackage {
  id: string
  name: string
  credits: number
  price_cents: number
  stripe_product_id: string | null
  stripe_price_id: string | null
  is_active: boolean
  sort_order: number
}

interface BlogPost {
  id: string
  slug: string
  title: string
  excerpt: string | null
  content: string
  cover_image_url: string | null
  tags: string[] | null
  is_published: boolean
  published_at: string | null
  created_at: string | null
}

export default function AdminPage() {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'tools' | 'analytics' | 'shopify' | 'blog' | 'pricing'>('overview')
  const [showToolForm, setShowToolForm] = useState(false)
  const [editingTool, setEditingTool] = useState<Tool | null>(null)
  const [toolForm, setToolForm] = useState({
    name: '',
    slug: '',
    description: '',
    credit_cost: 1,
    is_active: true,
  })
  const [showCreditModal, setShowCreditModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<{ id: string; name: string | null; email: string | null } | null>(null)
  const [creditAmount, setCreditAmount] = useState<number>(0)
  const [creditDescription, setCreditDescription] = useState<string>('')
  const [addingCredits, setAddingCredits] = useState(false)
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [plansLoading, setPlansLoading] = useState(false)
  const [editingPlanCredits, setEditingPlanCredits] = useState<Record<string, number>>({})
  const [savingPlan, setSavingPlan] = useState<string | null>(null)
  const [packages, setPackages] = useState<CreditPackage[]>([])
  const [packagesLoading, setPackagesLoading] = useState(false)
  const [editingPkgCredits, setEditingPkgCredits] = useState<Record<string, number>>({})
  const [editingPkgNames, setEditingPkgNames] = useState<Record<string, string>>({})
  const [savingPkg, setSavingPkg] = useState<string | null>(null)
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([])
  const [blogLoading, setBlogLoading] = useState(false)
  const [showBlogForm, setShowBlogForm] = useState(false)
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null)
  const [blogForm, setBlogForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    cover_image_url: '',
    tags: '',
    is_published: false,
    published_at: '',
  })
  const supabase = createClient()
  const navigate = useNavigate()
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://eutekssvzhtkwzperkzb.supabase.co/functions/v1'

  useEffect(() => {
    loadAdminStats()
    loadBlogPosts()
    loadPlans()
    loadPackages()
  }, [])

  const loadAdminStats = async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      
      if (sessionError || !session) {
        navigate('/auth/login')
        return
      }

      if (!session?.access_token) {
        setError('No access token available')
        setLoading(false)
        return
      }

      const response = await fetch(`${apiBaseUrl}/get-admin-stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data = await response.json()
        setStats(data)
      } else if (response.status === 403) {
        setError('Access denied. Admin privileges required.')
        setTimeout(() => navigate('/dashboard'), 2000)
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Failed to load admin stats' }))
        setError(errorData.message || 'Failed to load admin stats')
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateTool = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) return

      const response = await fetch(`${apiBaseUrl}/admin-tools`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'create',
          tool: toolForm,
        }),
      })

      if (response.ok) {
        setShowToolForm(false)
        setToolForm({ name: '', slug: '', description: '', credit_cost: 1, is_active: true })
        await loadAdminStats()
      } else {
        const errorData = await response.json()
        alert(errorData.message || 'Failed to create tool')
      }
    } catch (err: any) {
      alert(err.message || 'An error occurred')
    }
  }

  const handleUpdateTool = async () => {
    if (!editingTool) return

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) return

      const response = await fetch(`${apiBaseUrl}/admin-tools`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'update',
          tool: { ...toolForm, id: editingTool.id },
        }),
      })

      if (response.ok) {
        setShowToolForm(false)
        setEditingTool(null)
        setToolForm({ name: '', slug: '', description: '', credit_cost: 1, is_active: true })
        await loadAdminStats()
      } else {
        const errorData = await response.json()
        alert(errorData.message || 'Failed to update tool')
      }
    } catch (err: any) {
      alert(err.message || 'An error occurred')
    }
  }

  const handleDeleteTool = async (toolId: string) => {
    if (!confirm('Are you sure you want to delete this tool? This action cannot be undone.')) return

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) return

      const response = await fetch(`${apiBaseUrl}/admin-tools`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'delete',
          tool: { id: toolId },
        }),
      })

      if (response.ok) {
        await loadAdminStats()
      } else {
        const errorData = await response.json()
        alert(errorData.message || 'Failed to delete tool')
      }
    } catch (err: any) {
      alert(err.message || 'An error occurred')
    }
  }

  const openEditTool = (tool: Tool) => {
    setEditingTool(tool)
    setToolForm({
      name: tool.name,
      slug: tool.slug,
      description: tool.description || '',
      credit_cost: tool.credit_cost,
      is_active: tool.is_active,
    })
    setShowToolForm(true)
  }

  const openCreditModal = (user: { id: string; name: string | null; email: string | null }) => {
    setSelectedUser(user)
    setCreditAmount(0)
    setCreditDescription('')
    setShowCreditModal(true)
  }

  const handleAddCredits = async () => {
    if (!selectedUser || creditAmount <= 0) {
      alert('Please enter a valid credit amount')
      return
    }

    setAddingCredits(true)
    try {
      const { error } = await supabase.rpc('add_credits', {
        p_user_id: selectedUser.id,
        p_credits: creditAmount,
        p_transaction_type: 'adjustment',
        p_description: creditDescription || `Admin credit adjustment: ${creditAmount} credits`,
      })

      if (error) {
        throw error
      }

      alert(`Successfully added ${creditAmount} credits to ${selectedUser.email || selectedUser.name || 'user'}`)
      setShowCreditModal(false)
      setSelectedUser(null)
      setCreditAmount(0)
      setCreditDescription('')
      await loadAdminStats() // Refresh stats to show updated credits
    } catch (err: any) {
      alert(err.message || 'Failed to add credits')
    } finally {
      setAddingCredits(false)
    }
  }

  const loadPlans = async () => {
    setPlansLoading(true)
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('sort_order', { ascending: true })
      if (error) throw error
      const fetched = (data as SubscriptionPlan[]) || []
      setPlans(fetched)
      // Pre-fill editing values
      const credits: Record<string, number> = {}
      fetched.forEach((p) => { credits[p.id] = p.credits_per_period })
      setEditingPlanCredits(credits)
    } catch (err: any) {
      console.error('Error loading plans', err)
    } finally {
      setPlansLoading(false)
    }
  }

  const handleSavePlanCredits = async (planId: string) => {
    const newCredits = editingPlanCredits[planId]
    if (newCredits == null || newCredits < 0) return
    setSavingPlan(planId)
    try {
      const { error } = await supabase
        .from('subscription_plans')
        .update({ credits_per_period: newCredits })
        .eq('id', planId)
      if (error) throw error
      await loadPlans()
    } catch (err: any) {
      alert(err.message || 'Failed to update plan credits')
    } finally {
      setSavingPlan(null)
    }
  }

  const loadPackages = async () => {
    setPackagesLoading(true)
    try {
      const { data, error } = await supabase
        .from('credit_packages')
        .select('*')
        .order('sort_order', { ascending: true })
      if (error) throw error
      const fetched = (data as CreditPackage[]) || []
      setPackages(fetched)
      const credits: Record<string, number> = {}
      const names: Record<string, string> = {}
      fetched.forEach((p) => { credits[p.id] = p.credits; names[p.id] = p.name })
      setEditingPkgCredits(credits)
      setEditingPkgNames(names)
    } catch (err: any) {
      console.error('Error loading credit packages', err)
    } finally {
      setPackagesLoading(false)
    }
  }

  const handleSavePkg = async (pkgId: string) => {
    const newCredits = editingPkgCredits[pkgId]
    const newName = editingPkgNames[pkgId]
    if (newCredits == null || newCredits < 0) return
    setSavingPkg(pkgId)
    try {
      const update: Record<string, any> = { credits: newCredits }
      if (newName) update.name = newName
      const { error } = await supabase
        .from('credit_packages')
        .update(update)
        .eq('id', pkgId)
      if (error) throw error
      await loadPackages()
    } catch (err: any) {
      alert(err.message || 'Failed to update package')
    } finally {
      setSavingPkg(null)
    }
  }

  const loadBlogPosts = async () => {
    setBlogLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        setError('Not authenticated')
        return
      }

      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, slug, title, excerpt, content, cover_image_url, tags, is_published, published_at, created_at')
        .order('created_at', { ascending: false })

      if (error) {
        throw error
      }

      setBlogPosts((data as BlogPost[]) || [])
    } catch (err: any) {
      console.error('Error loading blog posts', err)
      setError(err.message || 'Failed to load blog posts')
    } finally {
      setBlogLoading(false)
    }
  }

  const resetBlogForm = () => {
    setEditingPost(null)
    setBlogForm({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      cover_image_url: '',
      tags: '',
      is_published: false,
      published_at: '',
    })
  }

  const openCreateBlogPost = () => {
    resetBlogForm()
    setShowBlogForm(true)
  }

  const openEditBlogPost = (post: BlogPost) => {
    setEditingPost(post)
    
    let dateStr = ''
    if (post.published_at) {
      try {
        const d = new Date(post.published_at)
        const offset = d.getTimezoneOffset()
        const localDate = new Date(d.getTime() - offset * 60 * 1000)
        dateStr = localDate.toISOString().slice(0, 16)
      } catch (e) {
        console.error('Error formatting publish date', e)
      }
    }

    setBlogForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt || '',
      content: post.content,
      cover_image_url: post.cover_image_url || '',
      tags: (post.tags || []).join(', '),
      is_published: post.is_published,
      published_at: dateStr,
    })
    setShowBlogForm(true)
  }

  const handleSaveBlogPost = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        setError('Not authenticated')
        return
      }

      const tagsArray = blogForm.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)

      const payload: Partial<BlogPost> = {
        title: blogForm.title,
        slug: blogForm.slug || blogForm.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
        excerpt: blogForm.excerpt || null,
        content: blogForm.content,
        cover_image_url: blogForm.cover_image_url || null,
        tags: tagsArray.length ? tagsArray : null,
        is_published: blogForm.is_published,
        published_at: blogForm.is_published
          ? (blogForm.published_at ? new Date(blogForm.published_at).toISOString() : (editingPost?.published_at || new Date().toISOString()))
          : null,
      }

      if (editingPost) {
        const { error } = await supabase
          .from('blog_posts')
          .update(payload)
          .eq('id', editingPost.id)

        if (error) {
          throw error
        }
      } else {
        const { error } = await supabase
          .from('blog_posts')
          .insert(payload)

        if (error) {
          throw error
        }
      }

      setShowBlogForm(false)
      resetBlogForm()
      await loadBlogPosts()
    } catch (err: any) {
      alert(err.message || 'Failed to save blog post')
    }
  }

  const handleDeleteBlogPost = async (postId: string) => {
    if (!confirm('Are you sure you want to delete this blog post? This action cannot be undone.')) return

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        setError('Not authenticated')
        return
      }

      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', postId)

      if (error) {
        throw error
      }

      await loadBlogPosts()
    } catch (err: any) {
      alert(err.message || 'Failed to delete blog post')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading admin statistics...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={loadAdminStats}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 transition-colors">
      <Sidebar />

      <div className="lg:pl-64">
        <DashboardHeader title="Admin Dashboard" />

        <main className="px-4 sm:px-6 lg:px-8 py-8">
          {/* Tabs */}
          <div className="mb-6 border-b border-gray-200 dark:border-slate-700">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('tools')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'tools'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Tools Management
              </button>
              <button
                onClick={() => setActiveTab('analytics')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'analytics'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Tool Analytics
              </button>
              <button
                onClick={() => setActiveTab('shopify')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'shopify'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Shopify Analytics
              </button>
              <button
                onClick={() => setActiveTab('pricing')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'pricing'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Pricing / Credits
              </button>
              <button
                onClick={() => setActiveTab('blog')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'blog'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Blog
              </button>
            </nav>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && stats?.summary && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Total Users</h3>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.summary.total_users}</p>
                </div>
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Active Subscriptions</h3>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.summary.active_subscriptions}</p>
                </div>
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Total Credits Earned</h3>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.summary.total_credits_earned.toLocaleString()}</p>
                </div>
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Total Credits Spent</h3>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.summary.total_credits_spent.toLocaleString()}</p>
                </div>
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Total Credits Balance</h3>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.summary.total_credits_balance.toLocaleString()}</p>
                </div>
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Usage This Month</h3>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.summary.total_usage_this_month.toLocaleString()}</p>
                </div>
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Total Usage (All Time)</h3>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.summary.total_usage_all_time.toLocaleString()}</p>
                </div>
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Usage by Source</h3>
                  <div className="mt-2 space-y-1">
                    {Object.entries(stats.summary.usage_by_source).map(([source, count]) => {
                      // Parse source to extract app/extension names
                      let displaySource = source;
                      let logo: string | undefined;
                      
                      if (source.startsWith('extension-')) {
                        const appName = source.replace('extension-', '').split('-').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ');
                        displaySource = `${appName} Extension`;
                      } else if (source.startsWith('app-')) {
                        const appName = source.replace('app-', '').split('-').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ');
                        displaySource = `${appName} App`;
                      } else if (source === 'wordpress') {
                        displaySource = 'WordPress';
                        logo = '/wordpress-logo3.webp';
                      } else if (source === 'shopify') {
                        displaySource = 'Shopify';
                        logo = '/shopify.png';
                      }
                      
                      return (
                        <div key={source} className="flex justify-between items-center text-sm">
                          <span className="text-gray-600 dark:text-gray-400 flex items-center gap-2">
                            {logo ? (
                              <img src={logo} alt={displaySource} className="w-4 h-4 object-contain" />
                            ) : null}
                            {displaySource}:
                          </span>
                          <span className="font-semibold text-gray-900 dark:text-white">{count.toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Users Table */}
              <div className="glass-card rounded-xl p-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">All Users</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-slate-700">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Name</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Joined</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Credits</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Subscription</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Usage (Month)</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Usage (All Time)</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats?.users.map((user) => (
                        <tr key={user.id} className="border-b border-gray-100 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition">
                          <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">
                            {user.name || 'N/A'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {user.email || 'N/A'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            <div className="space-y-1">
                              <div>Balance: <span className="font-semibold">{user.credits.balance.toLocaleString()}</span></div>
                              <div className="text-xs text-gray-500">Earned: {user.credits.total_earned.toLocaleString()}</div>
                              <div className="text-xs text-gray-500">Spent: {user.credits.total_spent.toLocaleString()}</div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {user.subscription ? (
                              <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-semibold">
                                {user.subscription.plan_name}
                              </span>
                            ) : (
                              <span className="text-gray-500 dark:text-gray-400 text-xs">Free</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {user.usage.this_month.toLocaleString()}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {user.usage.all_time.toLocaleString()}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            <button
                              onClick={() => openCreditModal({ id: user.id, name: user.name, email: user.email })}
                              className="px-3 py-1 bg-primary-600 text-white rounded text-xs hover:bg-primary-700 transition"
                            >
                              Add Credits
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Add Credits Modal */}
              {showCreditModal && selectedUser && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="glass-card rounded-xl p-6 max-w-md w-full mx-4">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                      Add Credits to {selectedUser.email || selectedUser.name || 'User'}
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Credit Amount *
                        </label>
                        <input
                          type="number"
                          value={creditAmount || ''}
                          onChange={(e) => setCreditAmount(parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="Enter credit amount"
                          min="1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Description (Optional)
                        </label>
                        <textarea
                          value={creditDescription}
                          onChange={(e) => setCreditDescription(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          rows={3}
                          placeholder="Reason for adding credits..."
                        />
                      </div>
                      <div className="flex space-x-3">
                        <button
                          onClick={handleAddCredits}
                          disabled={addingCredits || creditAmount <= 0}
                          className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {addingCredits ? 'Adding...' : 'Add Credits'}
                        </button>
                        <button
                          onClick={() => {
                            setShowCreditModal(false)
                            setSelectedUser(null)
                            setCreditAmount(0)
                            setCreditDescription('')
                          }}
                          disabled={addingCredits}
                          className="flex-1 px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 transition disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Tools Management Tab */}
          {activeTab === 'tools' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Tools Management</h2>
                <button
                  onClick={() => {
                    setEditingTool(null)
                    setToolForm({ name: '', slug: '', description: '', credit_cost: 1, is_active: true })
                    setShowToolForm(true)
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                >
                  + Add New Tool
                </button>
              </div>

              {/* Tool Form Modal */}
              {showToolForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="glass-card rounded-xl p-6 max-w-md w-full mx-4">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                      {editingTool ? 'Edit Tool' : 'Create New Tool'}
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Name *
                        </label>
                        <input
                          type="text"
                          value={toolForm.name}
                          onChange={(e) => setToolForm({ ...toolForm, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="Alt Text Generator"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Slug *
                        </label>
                        <input
                          type="text"
                          value={toolForm.slug}
                          onChange={(e) => setToolForm({ ...toolForm, slug: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="alt-text"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Description
                        </label>
                        <textarea
                          value={toolForm.description}
                          onChange={(e) => setToolForm({ ...toolForm, description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          rows={3}
                          placeholder="Tool description..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Credit Cost
                        </label>
                        <input
                          type="number"
                          value={toolForm.credit_cost}
                          onChange={(e) => setToolForm({ ...toolForm, credit_cost: parseInt(e.target.value) || 1 })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          min="1"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={toolForm.is_active}
                          onChange={(e) => setToolForm({ ...toolForm, is_active: e.target.checked })}
                          className="mr-2"
                        />
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Active
                        </label>
                      </div>
                      <div className="flex space-x-3">
                        <button
                          onClick={editingTool ? handleUpdateTool : handleCreateTool}
                          className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                        >
                          {editingTool ? 'Update' : 'Create'}
                        </button>
                        <button
                          onClick={() => {
                            setShowToolForm(false)
                            setEditingTool(null)
                            setToolForm({ name: '', slug: '', description: '', credit_cost: 1, is_active: true })
                          }}
                          className="flex-1 px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 transition"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tools List */}
              <div className="glass-card rounded-xl p-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-slate-700">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Name</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Slug</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Description</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Credit Cost</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats?.tools?.map((tool) => (
                        <tr key={tool.id} className="border-b border-gray-100 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition">
                          <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">{tool.name}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 font-mono">{tool.slug}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{tool.description || '-'}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{tool.credit_cost}</td>
                          <td className="py-3 px-4 text-sm">
                            {tool.is_active ? (
                              <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-semibold">
                                Active
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 rounded text-xs font-semibold">
                                Inactive
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => openEditTool(tool)}
                                className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs hover:bg-blue-200 dark:hover:bg-blue-900/50 transition"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteTool(tool.id)}
                                className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded text-xs hover:bg-red-200 dark:hover:bg-red-900/50 transition"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Tool Analytics Tab */}
          {activeTab === 'analytics' && stats?.tool_analytics && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Tool Analytics</h2>

              {/* Usage by Tool This Month */}
              <div className="glass-card rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Usage by Tool (This Month)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(stats.tool_analytics?.by_tool_this_month || {}).map(([slug, data]) => (
                    <div key={slug} className="p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white">{data.name}</h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">{slug}</p>
                        </div>
                      </div>
                      <div className="mt-3 space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Requests:</span>
                          <span className="font-semibold text-gray-900 dark:text-white">{data.count.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Credits Used:</span>
                          <span className="font-semibold text-gray-900 dark:text-white">{data.credits.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Usage by Tool and Source (Shopify breakdown) */}
              <div className="glass-card rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Usage by Tool and Source (This Month)</h3>
                <div className="space-y-4">
                  {Object.entries(stats.tool_analytics?.by_tool_and_source || {}).map(([toolSlug, sources]) => {
                    const toolName = stats.tool_analytics?.by_tool_this_month?.[toolSlug]?.name || toolSlug
                    return (
                      <div key={toolSlug} className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">{toolName}</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {Object.entries(sources).map(([source, data]) => {
                            // Parse source to extract app/extension names
                            let displaySource = source;
                            let logo: string | undefined;
                            
                            if (source.startsWith('extension-')) {
                              const appName = source.replace('extension-', '').split('-').map(word => 
                                word.charAt(0).toUpperCase() + word.slice(1)
                              ).join(' ');
                              displaySource = `${appName} Extension`;
                            } else if (source.startsWith('app-')) {
                              const appName = source.replace('app-', '').split('-').map(word => 
                                word.charAt(0).toUpperCase() + word.slice(1)
                              ).join(' ');
                              displaySource = `${appName} App`;
                      } else if (source === 'wordpress') {
                        displaySource = 'WordPress ATG';
                        logo = '/wordpress-logo3.webp';
                      } else if (source === 'shopify') {
                        displaySource = 'Shopify App ATG';
                        logo = '/shopify.png';
                      } else if (source === 'api') {
                        displaySource = 'API Call ATG';
                      } else if (source === 'extension') {
                        displaySource = 'Browser Extension ATG';
                      }
                            
                            return (
                              <div key={source} className="p-3 bg-gray-50 dark:bg-slate-800 rounded">
                                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize mb-1 flex items-center gap-2">
                                  {logo ? (
                                    <img src={logo} alt={displaySource} className="w-4 h-4 object-contain" />
                                  ) : (
                                    source === 'shopify' && <span>🛍️</span>
                                  )}
                                  {displaySource}
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                  <div>Requests: <span className="font-semibold">{data.count}</span></div>
                                  <div>Credits: <span className="font-semibold">{data.credits}</span></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Usage by Tool All Time */}
              <div className="glass-card rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Usage by Tool (All Time)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(stats.tool_analytics?.by_tool_all_time || {}).map(([slug, data]) => (
                    <div key={slug} className="p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white">{data.name}</h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">{slug}</p>
                        </div>
                      </div>
                      <div className="mt-3 space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Total Requests:</span>
                          <span className="font-semibold text-gray-900 dark:text-white">{data.count.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Total Credits:</span>
                          <span className="font-semibold text-gray-900 dark:text-white">{data.credits.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Shopify Analytics Tab */}
          {activeTab === 'shopify' && stats?.shopify_analytics && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Shopify Analytics</h2>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Total Shops</h3>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.shopify_analytics.total_shops}</p>
                </div>
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Active Subscriptions</h3>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.shopify_analytics.active_subscriptions}</p>
                </div>
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Total Revenue</h3>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    ${stats.shopify_analytics.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">One-Time Purchases</h3>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.shopify_analytics.total_one_time_purchases}</p>
                </div>
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Usage This Month</h3>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.shopify_analytics.usage_this_month.toLocaleString()}</p>
                </div>
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Usage All Time</h3>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.shopify_analytics.usage_all_time.toLocaleString()}</p>
                </div>
              </div>

              {/* Shops Table */}
              <div className="glass-card rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Shopify Shops</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-slate-700">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Shop Domain</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Shop Name</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.shopify_analytics.shops.map((shop: any, idx: number) => (
                        <tr key={idx} className="border-b border-gray-100 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition">
                          <td className="py-3 px-4 text-sm text-gray-900 dark:text-white font-mono">{shop.shop_domain || shop.domain || '-'}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{shop.shop_name || shop.name || '-'}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {shop.created_at ? new Date(shop.created_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Subscriptions Table */}
              <div className="glass-card rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Shopify Subscriptions</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-slate-700">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Shop</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Plan</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Amount</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.shopify_analytics.subscriptions.map((sub: any, idx: number) => (
                        <tr key={idx} className="border-b border-gray-100 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition">
                          <td className="py-3 px-4 text-sm text-gray-900 dark:text-white font-mono">{sub.shop_domain || sub.shop_id || '-'}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{sub.plan_name || sub.plan || '-'}</td>
                          <td className="py-3 px-4 text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              sub.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                              sub.status === 'trialing' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                              'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400'
                            }`}>
                              {sub.status || '-'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {sub.amount ? `$${parseFloat(sub.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Payment History Table */}
              <div className="glass-card rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Transaction History</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-slate-700">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Shop</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Amount</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Type</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.shopify_analytics.payments.map((payment: any, idx: number) => {
                        const transactionType = payment.transaction_type || payment.payment_type || payment.type || '-'
                        const typeLabels: Record<string, string> = {
                          subscription: 'Subscription',
                          one_time_purchase: 'One-Time Purchase',
                          usage: 'Usage',
                          refund: 'Refund',
                          adjustment: 'Adjustment',
                        }
                        const displayType = typeLabels[transactionType] || transactionType
                        return (
                        <tr key={idx} className="border-b border-gray-100 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition">
                          <td className="py-3 px-4 text-sm text-gray-900 dark:text-white font-mono">{payment.shop_domain || payment.shop_id || '-'}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 font-semibold">
                            {payment.amount ? parseFloat(payment.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 capitalize">{displayType}</td>
                          <td className="py-3 px-4 text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              payment.status === 'success' || payment.status === 'paid' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                              payment.status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                              'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400'
                            }`}>
                              {payment.status || '-'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {payment.created_at || payment.payment_date ? new Date(payment.created_at || payment.payment_date).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* One-Time Purchases Table */}
              <div className="glass-card rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">One-Time Purchases</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-slate-700">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Shop</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Credits</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Price</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.shopify_analytics.one_time_purchases.map((purchase: any, idx: number) => {
                        const credits = purchase.credits || purchase.credit_amount || purchase.credit_count
                        const price = purchase.amount || purchase.price || purchase.price_amount
                        return (
                        <tr key={idx} className="border-b border-gray-100 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition">
                          <td className="py-3 px-4 text-sm text-gray-900 dark:text-white font-mono">{purchase.shop_domain || purchase.shop_id || '-'}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 font-semibold">
                            {credits ? credits.toLocaleString() : '-'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 font-semibold">
                            {price ? parseFloat(price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              purchase.status === 'completed' || purchase.status === 'paid' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                              'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400'
                            }`}>
                              {purchase.status || '-'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {purchase.created_at || purchase.purchase_date ? new Date(purchase.created_at || purchase.purchase_date).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Pricing / Credits Tab */}
          {activeTab === 'pricing' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Subscription Plan Credits</h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Change the number of credits each plan provides per billing period. This does not change the price — only the credit allowance.
                </p>
              </div>

              {plansLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400" />
                </div>
              ) : plans.length === 0 ? (
                <div className="glass-card rounded-xl p-6 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No subscription plans found in the database. Add plans to the <code>subscription_plans</code> table first.
                  </p>
                </div>
              ) : (
                <div className="glass-card rounded-xl p-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-slate-700">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Plan</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Price</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Credits / Period</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plans.map((plan) => {
                          const priceDisplay = plan.price_monthly_cents === 0
                            ? 'Free'
                            : `$${(plan.price_monthly_cents / 100).toFixed(2)}/mo`
                          const hasChanged = editingPlanCredits[plan.id] !== plan.credits_per_period
                          return (
                            <tr key={plan.id} className="border-b border-gray-100 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition">
                              <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">
                                {plan.name}
                              </td>
                              <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                                {priceDisplay}
                              </td>
                              <td className="py-3 px-4">
                                <input
                                  type="number"
                                  min={0}
                                  value={editingPlanCredits[plan.id] ?? plan.credits_per_period}
                                  onChange={(e) =>
                                    setEditingPlanCredits({
                                      ...editingPlanCredits,
                                      [plan.id]: parseInt(e.target.value) || 0,
                                    })
                                  }
                                  className="w-28 px-3 py-1.5 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm"
                                />
                              </td>
                              <td className="py-3 px-4 text-sm">
                                {plan.is_active ? (
                                  <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-semibold">
                                    Active
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 rounded text-xs font-semibold">
                                    Inactive
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-sm">
                                <button
                                  onClick={() => handleSavePlanCredits(plan.id)}
                                  disabled={!hasChanged || savingPlan === plan.id}
                                  className={`px-3 py-1 rounded text-xs font-medium transition ${
                                    hasChanged
                                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                                      : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                                  }`}
                                >
                                  {savingPlan === plan.id ? 'Saving...' : 'Save'}
                                </button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                    Changes take effect immediately for new billing cycles. Existing subscribers keep their current credits until their next renewal.
                  </p>
                </div>
              )}

              {/* One-Time Credit Packages */}
              <div className="mt-10">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">One-Time Credit Packages</h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Change the number of credits each one-time purchase provides. This does not change the price — only the credit amount.
                </p>
              </div>

              {packagesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400" />
                </div>
              ) : packages.length === 0 ? (
                <div className="glass-card rounded-xl p-6 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No credit packages found in the database. Add packages to the <code>credit_packages</code> table first.
                  </p>
                </div>
              ) : (
                <div className="glass-card rounded-xl p-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-slate-700">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Package</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Price</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Credits</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {packages.map((pkg) => {
                          const priceDisplay = `$${(pkg.price_cents / 100).toFixed(2)}`
                          const hasChanged = editingPkgCredits[pkg.id] !== pkg.credits || editingPkgNames[pkg.id] !== pkg.name
                          return (
                            <tr key={pkg.id} className="border-b border-gray-100 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition">
                              <td className="py-3 px-4">
                                <input
                                  type="text"
                                  value={editingPkgNames[pkg.id] ?? pkg.name}
                                  onChange={(e) =>
                                    setEditingPkgNames({
                                      ...editingPkgNames,
                                      [pkg.id]: e.target.value,
                                    })
                                  }
                                  className="w-40 px-3 py-1.5 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm"
                                />
                              </td>
                              <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                                {priceDisplay}
                              </td>
                              <td className="py-3 px-4">
                                <input
                                  type="number"
                                  min={0}
                                  value={editingPkgCredits[pkg.id] ?? pkg.credits}
                                  onChange={(e) =>
                                    setEditingPkgCredits({
                                      ...editingPkgCredits,
                                      [pkg.id]: parseInt(e.target.value) || 0,
                                    })
                                  }
                                  className="w-28 px-3 py-1.5 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm"
                                />
                              </td>
                              <td className="py-3 px-4 text-sm">
                                {pkg.is_active ? (
                                  <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-semibold">
                                    Active
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 rounded text-xs font-semibold">
                                    Inactive
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-sm">
                                <button
                                  onClick={() => handleSavePkg(pkg.id)}
                                  disabled={!hasChanged || savingPkg === pkg.id}
                                  className={`px-3 py-1 rounded text-xs font-medium transition ${
                                    hasChanged
                                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                                      : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                                  }`}
                                >
                                  {savingPkg === pkg.id ? 'Saving...' : 'Save'}
                                </button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                    Changes apply to future purchases only. Users who have already bought a package keep the credits they received.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Blog Tab */}
          {activeTab === 'blog' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Blog Posts</h2>
                <button
                  onClick={openCreateBlogPost}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                >
                  + New Post
                </button>
              </div>

              {blogLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400" />
                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">Loading blog posts...</p>
                  </div>
                </div>
              )}

              {!blogLoading && blogPosts.length === 0 && (
                <div className="glass-card rounded-xl p-6 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No blog posts yet. Click &quot;New Post&quot; to create your first article.
                  </p>
                </div>
              )}

              {!blogLoading && blogPosts.length > 0 && (
                <div className="glass-card rounded-xl p-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-slate-700">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Title</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Slug</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Published</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Tags</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {blogPosts.map((post) => {
                          const dateString = post.published_at || post.created_at
                          const date = dateString ? new Date(dateString) : null
                          return (
                            <tr
                              key={post.id}
                              className="border-b border-gray-100 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition"
                            >
                              <td className="py-3 px-4 text-sm text-gray-900 dark:text-white max-w-xs truncate">
                                {post.title}
                              </td>
                              <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 font-mono max-w-xs truncate">
                                {post.slug}
                              </td>
                              <td className="py-3 px-4 text-sm">
                                {post.is_published ? (
                                  <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-semibold">
                                    Published
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded text-xs font-semibold">
                                    Draft
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                                {date ? date.toLocaleDateString() : '—'}
                              </td>
                              <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                                {(post.tags || []).join(', ')}
                              </td>
                              <td className="py-3 px-4 text-sm">
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => openEditBlogPost(post)}
                                    className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs hover:bg-blue-200 dark:hover:bg-blue-900/50 transition"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteBlogPost(post.id)}
                                    className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded text-xs hover:bg-red-200 dark:hover:bg-red-900/50 transition"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Blog Form Modal */}
              {showBlogForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="glass-card rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                      {editingPost ? 'Edit Blog Post' : 'New Blog Post'}
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Title *
                        </label>
                        <input
                          type="text"
                          value={blogForm.title}
                          onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="Post title"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Slug
                        </label>
                        <input
                          type="text"
                          value={blogForm.slug}
                          onChange={(e) => setBlogForm({ ...blogForm, slug: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="post-slug (optional, auto-generated from title)"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Excerpt
                        </label>
                        <textarea
                          value={blogForm.excerpt}
                          onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          rows={3}
                          placeholder="Short summary for list pages and social sharing"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Content *
                        </label>
                        <textarea
                          value={blogForm.content}
                          onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white font-mono text-sm"
                          rows={10}
                          placeholder="Write your blog post in Markdown or plain text..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Cover Image URL
                        </label>
                        <input
                          type="text"
                          value={blogForm.cover_image_url}
                          onChange={(e) => setBlogForm({ ...blogForm, cover_image_url: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="https://..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Tags (comma-separated)
                        </label>
                        <input
                          type="text"
                          value={blogForm.tags}
                          onChange={(e) => setBlogForm({ ...blogForm, tags: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="ai, alt text, accessibility"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          id="blog_is_published"
                          type="checkbox"
                          checked={blogForm.is_published}
                          onChange={(e) => setBlogForm({ ...blogForm, is_published: e.target.checked })}
                          className="mr-2"
                        />
                        <label
                          htmlFor="blog_is_published"
                          className="text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Published
                        </label>
                      </div>
                      {blogForm.is_published && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Publish Date / Time
                          </label>
                          <input
                            type="datetime-local"
                            value={blogForm.published_at}
                            onChange={(e) => setBlogForm({ ...blogForm, published_at: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          />
                        </div>
                      )}
                      <div className="flex space-x-3 pt-2">
                        <button
                          onClick={handleSaveBlogPost}
                          className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                        >
                          {editingPost ? 'Save Changes' : 'Create Post'}
                        </button>
                        <button
                          onClick={() => {
                            setShowBlogForm(false)
                            resetBlogForm()
                          }}
                          className="flex-1 px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 transition"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  )
}
